import { Accordion, Form, Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'redux/store';
import { useState, useEffect } from 'react';
import { updateUser } from '../../redux/slices/auth';
import { DragDropContext, Draggable, DropResult } from 'react-beautiful-dnd';
import PhoenixDroppable from 'components/base/PhoenixDroppable';

interface ColumnDef {
  key: string;
  label: string;
}
interface Preferred {
  headerKey: string;
  header: string;
  checked: boolean;
  order: number;
}

interface ColumnSelectorProps {
  columns: ColumnDef[];
  pageName: string;
}

const ColumnSelectorRefactor: React.FC<ColumnSelectorProps> = ({ columns, pageName }) => {
  const dispatch = useAppDispatch();
  const userData: any = useSelector((s: any) => s.auth.userData);
  const preferred: Array<{ header: string; checked: boolean; order?: number }> = userData?.user_settings?.pageSettings?.[pageName]?.preferredColumns || [];

  // build initial list merging saved prefs (by label) with your columns[] (by key)
  const initial: Preferred[] = columns
    .map((col, index) => {
      const preferredColumn = preferred.find((p) => p.header === col.label);
      const preferredIndex = preferred.findIndex((p) => p.header === col.label);
      return {
        headerKey: col.key,
        header: col.label,
        checked: preferredColumn?.checked ?? false,
        order: typeof preferredColumn?.order === 'number' ? preferredColumn.order : preferredIndex >= 0 ? preferredIndex : preferred.length + index,
      };
    })
    .sort((a, b) => a.order - b.order)
    .map((column, index) => ({ ...column, order: index }));

  const [selected, setSelected] = useState<Preferred[]>(initial);

  // if userData changes, re-sync:
  useEffect(() => setSelected(initial), [userData?.user_settings]);

  const onSelect = (key: string) => {
    setSelected((sel) => sel.map((c) => (c.headerKey === key ? { ...c, checked: !c.checked } : c)));
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;
    const destinationIndex = result.destination.index;

    setSelected((current) => {
      const updated = [...current];
      const [movedColumn] = updated.splice(result.source.index, 1);
      updated.splice(destinationIndex, 0, movedColumn);
      return updated.map((column, columnIndex) => ({
        ...column,
        order: columnIndex,
      }));
    });
  };

  const onSave = async () => {
    const payload = {
      user_settings: {
        ...userData.user_settings,
        pageSettings: {
          ...userData.user_settings.pageSettings,
          [pageName]: {
            ...userData.user_settings.pageSettings?.[pageName],
            preferredColumns: selected.map((column, index) => ({
              ...column,
              order: index,
            })),
          },
        },
      },
    };
    try {
      await dispatch(updateUser(payload));
    } catch (err) {
      console.error('Failed to save columns', err);
    }
  };

  return (
    <Accordion flush className="p-0">
      <Accordion.Item eventKey="0">
        <Accordion.Header>Preferred columns</Accordion.Header>
        <Accordion.Body>
          <Form>
            <Form.Group className="mb-2">
              <Form.Label>Select the columns that should be visible</Form.Label>
              <DragDropContext onDragEnd={onDragEnd}>
                <PhoenixDroppable droppableId={`preferred-columns-${pageName}`}>
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {selected.map((col, index) => (
                        <Draggable key={col.headerKey} draggableId={String(col.headerKey)} index={index}>
                          {(provided) => (
                            <div ref={provided.innerRef} {...provided.draggableProps} className="d-flex align-items-center justify-content-between w-100 mb-2">
                              <Form.Check
                                type="checkbox"
                                id={col.headerKey}
                                label={col.header}
                                value={col.headerKey}
                                checked={col.checked}
                                onChange={() => onSelect(col.headerKey)}
                                className="mb-0"
                              />
                              <span
                                {...provided.dragHandleProps}
                                aria-label="Drag column"
                                title="Drag to reorder"
                                className="text-body-tertiary px-1"
                                style={{ cursor: 'grab', userSelect: 'none' }}
                              >
                                ::
                              </span>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </PhoenixDroppable>
              </DragDropContext>
            </Form.Group>
            <Button onClick={onSave}>Save columns</Button>
          </Form>
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};

export default ColumnSelectorRefactor;
